import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_PSYCHOPATH,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";
import {
  calculatePsychopathAssessmentScores,
  getPsychopathCombinationContent,
  getPsychopathLevel,
  getPsychopathOverallLevel,
  getPsychopathTraitScores,
} from "../../utils/assessment-utils";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  convertToLowerCase,
  getEnneagramBannerImage,
  getPsycReportPageImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";
import ExpandableSection from "../../components/expandable-collapsible/component";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import PathHelper from "../../utils/path-helper";
import ReportWrapper from "../../components/report-wrapper";

const PsychopathyTestResultsReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,

  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,

  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_PSYCHOPATH[currentLocale] ||
            MODEL_TYPE_PSYCHOPATH[LOCALE.EN],

          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const result1ImageText = "psyc-result-1";
  const result1Image = getPsycReportPageImage(result1ImageText);
  const bannerImage = getEnneagramBannerImage();
  const result2ImageText = "psyc-result-2";
  const result2Image = getPsycReportPageImage(result2ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const progressBarColor = ["#4CAF50", "#BF40BF"];

  const TakeTestButton = ({ onClick, id }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id={id}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              Discover your true personality with our Enneagram Assessment.
            </h3>
            <p className="text-white opacity-90 text-center">
              Start your journey of self-awareness today!
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              Take the Test
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              Unlock your potential with the DiSC Assessment! Gain valuable
              insights.
            </h3>
            <p className="text-white opacity-90 text-center">
              Take the assessment now!
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              Take the Test
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {
                "Find out your personality code! Take our Personality Test and find out which of the 16 personalities you are!"
              }
            </h3>
            <p className="text-white opacity-90 text-center">
              Discover more about your personality type
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              Take the Test
            </button>
          </div>
        </div>
      </section>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-4xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const ContentCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    level,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {heading + ": "}{" "}
                  <span className="text-[#805bec]">{level}</span>
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const ProgressCircle = ({
    color = "#805aed",
    duration = 2000,
    targetPercentage = 50,
  }) => {
    const [percentage, setPercentage] = useState(0);
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const intervalTime = 30; // Time in milliseconds for each update

    useEffect(() => {
      const steps = duration / intervalTime; // Number of updates
      const stepSize = targetPercentage / steps; // Percentage to increase per step

      let currentPercentage = 0;
      const interval = setInterval(() => {
        currentPercentage += stepSize;
        if (currentPercentage >= targetPercentage) {
          currentPercentage = targetPercentage;
          clearInterval(interval);
        }
        setPercentage(parseInt(currentPercentage)); // Ensure whole numbers
      }, intervalTime);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }, [duration, targetPercentage]);

    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%] ease-in duration-100">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Animated Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: "stroke-dashoffset 0.1s ease-in-out",
              }}
            />
          </svg>

          {/* Percentage Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-2xl font-bold text-gray-700">
            <span>{targetPercentage}%</span>
          </div>
        </div>
      </div>
    );
  };

  const NextStepsCard = ({
    heading,
    intro,
    bgClr,
    contentArray,
    overview,
    character,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full"
      >
        {/* HEADING */}
        {heading && (
          <div className="flex items-start gap-2 mb-3">
            {character && (
              <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={character} size={20} />
              </div>
            )}

            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {heading}
              </h2>

              {/* OPTIONAL INTRO */}
              {intro && (
                <p className="text-gray-600 text-sm md:text-base mt-2 leading-relaxed">
                  {intro}
                </p>
              )}
            </div>
          </div>
        )}

        {/* OVERVIEW */}
        {overview && (
          <p className="text-gray-700 text-base md:text-lg mb-4">{overview}</p>
        )}

        {/* CONTENT LIST */}
        {contentArray && (
          <ul className="space-y-4 mt-2">
            {contentArray.map((item, index) => (
              <li key={index} className="flex gap-3 items-start">
                {/* BULLET */}
                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-1">
                  <CircleIcon character={""} size={16} />
                </div>

                {/* TEXT / LINK */}
                <div className="flex flex-col">
                  {/* Optional Heading */}
                  {item?.Heading && (
                    <h3 className="text-base font-semibold text-gray-700">
                      {item.Heading}
                    </h3>
                  )}

                  {/* Content (Clickable if link exists) */}
                  {item?.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 text-base md:text-lg leading-relaxed hover:text-blue-600 hover:underline transition break-words"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words">
                      {item.label}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const impulsivityScore = userTraits.find(
    (item) => item.trait.title === "impulsivity",
  )?.score;

  const sociopathyScore = userTraits.find(
    (item) => item.trait.title === "sociopathy",
  )?.score;

  const impulsivityScoreLevel = getPsychopathLevel(impulsivityScore);

  const sociopathyScoreLevel = getPsychopathLevel(sociopathyScore);

  const overAllScore = calculatePsychopathAssessmentScores(
    sociopathyScore,
    impulsivityScore,
  ).overallScore;

  const psychopathDetailedScores = calculatePsychopathAssessmentScores(
    sociopathyScore,
    impulsivityScore,
  );

  const calculatedSociopathyScore =
    psychopathDetailedScores?.sociopathyScore ?? 0;

  const calculatedImpulsivityScore =
    psychopathDetailedScores?.impulsivityScore ?? 0;

  const progressScore = userTraits?.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];

    const traitTitle = item?.trait?.title;

    let calculatedPercentage = item?.percentage;

    if (traitTitle === "sociopathy") {
      calculatedPercentage = calculatedSociopathyScore;
    }

    if (traitTitle === "impulsivity") {
      calculatedPercentage = calculatedImpulsivityScore;
    }

    return {
      label: snakeToTitleCase(item?.trait?.[localeKey]),
      percentage: calculatedPercentage,
      color: progressBarColor[index % progressBarColor.length],
    };
  });

  const overAllLevel = getPsychopathOverallLevel(overAllScore);

  const getYourInsightContent = getPsychopathCombinationContent(
    contentData?.personalizedBreakdown?.combinations,
    sociopathyScoreLevel,
    impulsivityScoreLevel,
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              {/* SECTION 1 - OVERVIEW */}
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-6 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
                      {/* LEFT - ABOUT */}
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {t("report.psychopathy.about_test")}
                        </h3>
                        <div className="mt-8">
                          <p className="text-base text-gray-700 leading-relaxed">
                            {contentData?.intro}
                          </p>
                        </div>
                      </div>

                      {/* CENTER - OVERALL RESULT */}
                      <div className="flex flex-col items-center justify-start text-center h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {contentData?.overallResult?.heading}:
                        </h3>

                        <div className="mt-3 flex justify-center items-center w-full">
                          <ProgressCircle
                            targetPercentage={
                              calculatePsychopathAssessmentScores(
                                sociopathyScore,
                                impulsivityScore,
                              ).overallScore
                            }
                          />
                        </div>
                      </div>

                      {/* RIGHT - TRAIT SCORE */}
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {t("report.psychopathy.trait_score_label")}:
                        </h3>

                        {/* UPDATED DESCRIPTION */}
                        <p className="text-gray-600 max-w-sm mb-3 leading-relaxed">
                          {t("report.psychopathy.main_factors")}
                        </p>

                        {/* UPDATED PROGRESS SECTION */}
                        <div className="w-full flex justify-start">
                          <div className="w-full max-w-sm">
                            <ul className="mt-8">
                              {progressScore.map((item) => {
                                return (
                                  <ProgressBar
                                    key={item.label}
                                    label={item.label}
                                    percentage={item.percentage}
                                    color={item.color}
                                  />
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* DISCLAIMER */}
                    <div className="mt-8">
                      <DescriptiveCard
                        heading={
                          contentData?.overallResult?.bands[overAllLevel].label
                        }
                        overview={
                          contentData?.overallResult?.bands[overAllLevel]
                            .description
                        }
                        bgClr="bg-blue-50"
                        textClr="text-yellow-800"
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.howScored?.heading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {contentData?.howScored?.content}
                    </p>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 2 - UNDERSTANDING RESULTS */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.psychopathy.understanding_your_results")}
                      color="purple"
                    />
                  }
                >
                  <div className="p-6 space-y-8">
                    {/* LEFT CARDS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                      {/* CARD 1 */}
                      <ContentCard
                        heading={contentData?.factors?.sociopathy?.label}
                        level={
                          contentData?.factors?.sociopathy?.bands?.[
                            sociopathyScoreLevel
                          ]?.label
                        }
                        overview={
                          contentData?.factors?.sociopathy?.bands?.[
                            sociopathyScoreLevel
                          ]?.description
                        }
                        bgClr="#fff4e6"
                      />

                      {/* CARD 2 */}
                      <ContentCard
                        heading={contentData?.factors?.impulsiveness?.label}
                        level={
                          contentData?.factors?.impulsiveness?.bands?.[
                            impulsivityScoreLevel
                          ]?.label
                        }
                        overview={
                          contentData?.factors?.impulsiveness?.bands?.[
                            impulsivityScoreLevel
                          ]?.description
                        }
                        bgClr="#ffe6e6"
                      />
                    </div>

                    {/* INSIGHT */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
                      {/* INSIGHT CARD */}
                      <div className="bg-blue-50 p-4 rounded-xl shadow-lg h-full">
                        <p className="text-lg font-semibold text-gray-800 mb-3">
                          {contentData?.personalizedBreakdown?.heading}
                        </p>

                        <ul className="list-none pl-0 space-y-3">
                          {getYourInsightContent?.map((item, index) => (
                            <li
                              key={index}
                              className="leading-relaxed text-gray-700"
                            >
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* IMAGE */}
                      <div className="flex justify-center md:justify-center">
                        <img
                          src={result1Image}
                          alt="Psychopathy1"
                          className="w-full max-w-[500px] object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 4 - HELP + RESOURCES */}
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.seekHelp?.heading}
                      color="green"
                    />
                  }
                >
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                    {/* LEFT - LONG TEXT */}
                    <div className="h-full flex">
                      <div className="bg-[#e7feeb] rounded-xl shadow-lg p-6 flex flex-col w-full">
                        <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                          {contentData?.seekHelp?.content}
                        </p>

                        <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line mt-5">
                          {contentData?.seekHelp?.addendums[overAllLevel]}
                        </p>
                      </div>
                    </div>

                    {/* RIGHT - IMAGE + NEXT STEPS CARD */}
                    <div className="flex flex-col h-full">
                      {/* IMAGE AREA */}
                      <div className="flex-1 flex justify-center items-center mb-4">
                        <img
                          src={result2Image}
                          alt="Psychopathy"
                          className="max-h-[400px] w-auto object-contain"
                        />
                      </div>

                      {/* RESOURCE CARD - STICKS TO BOTTOM */}
                      <div className="mt-auto">
                        <NextStepsCard
                          heading={contentData?.resources?.heading}
                          intro={contentData?.resources?.intro}
                          character={"i"}
                          bgClr="#eff4fd"
                          contentArray={contentData?.resources?.items}
                        />
                      </div>
                    </div>
                  </div>

                  {/* DISCLAIMER */}
                  <div className="p-6 pt-0">
                    <DescriptiveCard
                      heading={contentData?.disclaimer?.heading}
                      overview={contentData?.disclaimer?.content}
                      bgClr="bg-red-50"
                      textClr="text-yellow-800"
                    />
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                CallToActionSectionForUnAuth(contentData)
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PsychopathyTestResultsReport;
